import { Context, Data, Schema } from 'effect';
import type { Effect, Layer } from 'effect';

import { ErrorTag } from '../../data/error';
import type { CookiesStore } from '../cookies/CookiesStore';
import { ContextTag } from '../context';
import { placementFromId, placementIdOf, PlacementSchema } from '../shipping/Placement';
import type { Placement } from '../shipping/Placement';
import { PlacementIdSchema } from '../shipping/PlacementId';
import type { PlacementId } from '../shipping/PlacementId';

export const ShippingMethodCodeSchema = Schema.NonEmptyString.pipe(
  Schema.brand('ShippingMethod.code')
);
export const PaymentMethodCodeSchema = Schema.NonEmptyString.pipe(
  Schema.brand('PaymentMethod.code')
);
export type ShippingMethodCode = typeof ShippingMethodCodeSchema.Type;
export type PaymentMethodCode = typeof PaymentMethodCodeSchema.Type;
export const OrderShipmentIdSchema = Schema.Finite.check(Schema.isInt()).pipe(
  Schema.brand('Order.shipmentId')
);
export const OrderPaymentIdSchema = Schema.Finite.check(Schema.isInt()).pipe(
  Schema.brand('Order.paymentId')
);
export type OrderShipmentId = typeof OrderShipmentIdSchema.Type;
export type OrderPaymentId = typeof OrderPaymentIdSchema.Type;

export const OrderShipmentSchema = Schema.Struct({
  shippingMethodCode: ShippingMethodCodeSchema,
  shippingPrice: Schema.Finite,
  shippingPriceWithoutVat: Schema.Finite,
  shippingName: Schema.String,
});

export const OrderPaymentSchema = Schema.Struct({
  paymentCode: PaymentMethodCodeSchema,
  paymentPrice: Schema.Finite,
  paymentPriceWithoutVat: Schema.Finite,
  paymentName: Schema.String,
});

export const OrderShipmentStorageSchema = Schema.Struct({
  /**
   * Where the parcel goes, with the detail the checkout redraws its pickup box
   * from. A cookie written by the previous release has no such field, so it is
   * optional and `storedPlacement` falls back to `placementId`.
   */
  placement: Schema.optionalKey(PlacementSchema),
  /**
   * The same placement reduced to the one field the backend takes.
   *
   * It is redundant with `placement` and is written for one release only: the
   * previous release requires it, so keeping it lets a rollback read the
   * cookies this release wrote. Remove it once no instance reads it.
   */
  placementId: Schema.NullOr(PlacementIdSchema),
  shipment: OrderShipmentSchema,
  payment: OrderPaymentSchema,
});

export const OrderShipmentStorageJsonSchema = Schema.fromJsonString(OrderShipmentStorageSchema);

export type OrderShipmentStorageT = Schema.Schema.Type<typeof OrderShipmentStorageSchema>;

/**
 * The placement an order shipment carries, whichever field holds it.
 *
 * A cookie this release wrote carries `placement`. One the previous release
 * wrote carries only `placementId`, which reads as `Predefined`.
 */
export const storedPlacement = (storage: OrderShipmentStorageT): Placement =>
  storage.placement ?? placementFromId(storage.placementId);

/** The placement id an order shipment carries, which is what the backend receives. */
export const storedPlacementId = (storage: OrderShipmentStorageT): PlacementId | null =>
  placementIdOf(storedPlacement(storage));

/**
 * Builds an order shipment around a placement.
 *
 * `placementId` is derived here and nowhere else, so the two placement fields
 * cannot drift apart.
 */
export const orderShipmentStorage = (parts: {
  readonly placement: Placement;
  readonly shipment: Schema.Codec.Encoded<typeof OrderShipmentSchema>;
  readonly payment: Schema.Codec.Encoded<typeof OrderPaymentSchema>;
}): OrderShipmentStorageT => ({
  placement: parts.placement,
  placementId: placementIdOf(parts.placement),
  shipment: Schema.decodeSync(OrderShipmentSchema)(parts.shipment),
  payment: Schema.decodeSync(OrderPaymentSchema)(parts.payment),
});

export class GetOrderShipmentError extends Data.TaggedError(ErrorTag.OrderShipmentGetError)<{
  message: string;
  cause?: unknown;
}> {}

export class SetOrderShipmentError extends Data.TaggedError(ErrorTag.OrderShipmentSetError)<{
  message: string;
  cause?: unknown;
}> {}

export type OrderShipmentService = {
  readonly getOrderShipment: Effect.Effect<OrderShipmentStorageT | null, GetOrderShipmentError>;
  readonly setOrderShipment: (
    orderShipment: OrderShipmentStorageT
  ) => Effect.Effect<void, SetOrderShipmentError | Schema.SchemaError>;
  readonly removeOrderShipment: Effect.Effect<void, SetOrderShipmentError>;
};

export class OrderShipment extends Context.Service<OrderShipment, OrderShipmentService>()(
  ContextTag.OrderShipment
) {
  declare static Live: Layer.Layer<OrderShipment, never, CookiesStore>;
}
